import { useState, type FormEvent } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { PlanMember, PlanRole } from "@travel/shared";
import { api, ApiError } from "../../api/client";
import { useAuth } from "../../contexts/AuthContext";
import { BottomSheet } from "../common/BottomSheet";
import { Button } from "../common/Button";
import { ConfirmDialog } from "../common/ConfirmDialog";

interface Props {
  open: boolean;
  planId: string;
  myRole: PlanRole;
  onClose: () => void;
}

export function MembersDialog({ open, planId, myRole, onClose }: Props) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const { user } = useAuth();

  const { data, isLoading } = useQuery({
    queryKey: ["members", planId],
    queryFn: () => api.members.list(planId).then((r) => r.members),
    enabled: open,
  });

  const [email, setEmail] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [removeTarget, setRemoveTarget] = useState<PlanMember | null>(null);

  const inviteMutation = useMutation({
    mutationFn: (email: string) => api.members.invite(planId, email),
    onSuccess: () => {
      setEmail("");
      setError(null);
      qc.invalidateQueries({ queryKey: ["members", planId] });
    },
    onError: (e: unknown) => {
      setError(e instanceof ApiError ? e.message : "초대 실패");
    },
  });

  const removeMutation = useMutation({
    mutationFn: (userId: string) => api.members.remove(planId, userId),
    onSuccess: (_void, removedUserId) => {
      qc.invalidateQueries({ queryKey: ["members", planId] });
      qc.invalidateQueries({ queryKey: ["plans"] });
      if (removedUserId === user?.id) {
        onClose();
        navigate("/", { replace: true });
      }
    },
  });

  function onInvite(e: FormEvent) {
    e.preventDefault();
    setError(null);
    const trimmed = email.trim();
    if (!trimmed) return;
    inviteMutation.mutate(trimmed);
  }

  return (
    <>
      <BottomSheet open={open && !removeTarget} onClose={onClose} title="멤버 관리">
        <div className="px-4 pb-4 space-y-4">
          {myRole === "owner" && (
            <form onSubmit={onInvite} className="space-y-2">
              <label className="block">
                <span className="text-sm text-slate-600 mb-1 block">이메일로 초대</span>
                <div className="flex gap-2">
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="flex-1 px-3 py-2.5 border border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-slate-900 min-h-[44px]"
                  />
                  <Button type="submit" disabled={inviteMutation.isPending || !email.trim()}>
                    {inviteMutation.isPending ? "초대 중…" : "초대"}
                  </Button>
                </div>
              </label>
              {error && (
                <pre className="bg-rose-50 text-rose-700 text-xs rounded-xl p-3 whitespace-pre-wrap">
                  {error}
                </pre>
              )}
              <p className="text-xs text-slate-500">
                이미 회원가입한 사용자만 초대할 수 있어요.
              </p>
            </form>
          )}

          <div>
            <h3 className="text-sm text-slate-600 mb-2">멤버 ({data?.length ?? 0})</h3>
            {isLoading ? (
              <p className="text-slate-500 text-sm">불러오는 중…</p>
            ) : (
              <ul className="divide-y divide-slate-100 border border-slate-200 rounded-xl overflow-hidden">
                {(data ?? []).map((m) => {
                  const isMe = m.userId === user?.id;
                  const canRemove =
                    (myRole === "owner" && m.role !== "owner") ||
                    (isMe && m.role !== "owner");
                  return (
                    <li
                      key={m.userId}
                      className="px-3 py-2.5 flex items-center gap-2"
                    >
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium text-slate-900 truncate">
                          {m.name}
                          {isMe && <span className="text-xs text-slate-400 ml-1">(나)</span>}
                        </div>
                        <div className="text-xs text-slate-500 truncate">{m.email}</div>
                      </div>
                      <span
                        className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                          m.role === "owner"
                            ? "bg-amber-100 text-amber-800"
                            : "bg-slate-100 text-slate-600"
                        }`}
                      >
                        {m.role === "owner" ? "owner" : "member"}
                      </span>
                      {canRemove && (
                        <button
                          type="button"
                          onClick={() => setRemoveTarget(m)}
                          className="text-xs text-rose-600 hover:text-rose-700 px-2 py-1 rounded-lg min-h-[36px]"
                        >
                          {isMe ? "나가기" : "제거"}
                        </button>
                      )}
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </div>
      </BottomSheet>

      <ConfirmDialog
        open={!!removeTarget}
        onClose={() => setRemoveTarget(null)}
        onConfirm={() => {
          if (removeTarget) removeMutation.mutate(removeTarget.userId);
          setRemoveTarget(null);
        }}
        title={
          removeTarget?.userId === user?.id
            ? "이 plan에서 나가시겠어요?"
            : `${removeTarget?.name ?? ""}님을 제거할까요?`
        }
        confirmLabel={removeTarget?.userId === user?.id ? "나가기" : "제거"}
        destructive
      />
    </>
  );
}
